import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Form, Tooltip, Tag, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemService } from '../../../Services/Manage/ItemService';
import { GoodsReceiptService } from '../../../Services/Manage/GoodsReceiptService';

import { StockLedgerService } from '../../../Services/Manage/StockLedgerService';

import { PurchaseOrderService } from '../../../Services/Manage/PurchaseOrderService';

import {
    EditOutlined, EyeOutlined, AppstoreOutlined, DollarOutlined,
    WarningOutlined, StopOutlined, DownloadOutlined, PlusOutlined,
    FileTextOutlined, FilterOutlined, CalendarOutlined, DownOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STOCK_STATUS_COLORS = {
    "In Stock": "#52c41a",
    "Low Stock": "#fa8c16",
    "Out of Stock": "#f5222d",
    "Inactive": "#8c8c8c",
};

const CATEGORY_PALETTE = ["#1677ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96", "#f5222d"];

// Same StatCard visual language as ProjectsPage.jsx — 11px label, 22px value, 36px icon circle.
const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const getStockStatus = (item, receivedQtyMap = {}) => {
    if (!item.is_active) return "Inactive";
    const stock = receivedQtyMap[String(item.id)] ?? Number(item.current_stock || 0);
    const reorder = Number(item.reorder_level || 0);
    if (stock <= 0) return "Out of Stock";
    if (stock <= reorder) return "Low Stock";
    return "In Stock";
};

const InventoryDashboard = () => {
    const perm = getPermission('/inventory');
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allItems, setAllItems] = useState([]);
    const [receivedQtyMap, setReceivedQtyMap] = useState({});
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
 const [pendingPOsCount, setPendingPOsCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
    const [dateFilterLabel, setDateFilterLabel] = useState('All Time');


    const getAllItems = async () => {
        setLoading(true);
        try {
            const request = await ItemService.getAllItem();
            setAllItems(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getReceivedQtyMap = async () => {
        try {
            const request = await StockLedgerService.getOnHandMap();
            setReceivedQtyMap(request.data || {});
        } catch (error) { console.error(error); }
    };

    const getPendingPOsCount = async () => {
        try {
            const request = await PurchaseOrderService.getAllPO();
            const count = (request.data || []).filter((po) => po.status === "Pending").length;
            setPendingPOsCount(count);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllItems();
        getReceivedQtyMap();
        getPendingPOsCount();
    }, []);

    // ---- Base scope respecting view_all permission ----
const visibleItems = useMemo(
        () => perm.view_all ? allItems : allItems.filter((item) => String(item.added_by) === String(currentUserId)),
        [allItems, perm.view_all, currentUserId]
    );

    // Date-filtered base for stat cards/charts — reacts to the "More Actions"
    // date preset only, not search/category/supplier/status.
    const dateFilteredItems = useMemo(() => {
        if (!dateFilterRange) return visibleItems;
        return visibleItems.filter((item) => {
            const d = dayjs(item.created_at);
            return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
        });
    }, [visibleItems, dateFilterRange]);

    const filteredItems = useMemo(() => {
        let filtered = visibleItems;

        if (searchText) {
            filtered = filtered.filter((item) =>
                Object.values(item).some((value) =>
                    value?.toString().toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        if (selectedCategory) {
            filtered = filtered.filter((item) => item.category === selectedCategory);
        }

        if (selectedSupplier) {
            filtered = filtered.filter((item) => item.supplier === selectedSupplier);
        }

        if (selectedStatus) {
            filtered = filtered.filter((item) => getStockStatus(item, receivedQtyMap) === selectedStatus);
        }

        if (dateFilterRange) {
            filtered = filtered.filter((item) => {
                const d = dayjs(item.created_at);
                return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
            });
        }

        return filtered;
    }, [visibleItems, searchText, selectedCategory, selectedStatus, selectedSupplier, receivedQtyMap, dateFilterRange]);

    // ---- Stats ----
    const totalItems = visibleItems.length; // always all-time

    const totalStockValue = useMemo(
        () => dateFilteredItems.reduce((s, i) => {
            const stock = receivedQtyMap[String(i.id)] ?? Number(i.current_stock || 0);
            return s + stock * Number(i.cost_price || 0);
        }, 0),
        [dateFilteredItems, receivedQtyMap]
    );

    const lowStockCount = useMemo(
        () => dateFilteredItems.filter((i) => getStockStatus(i, receivedQtyMap) === "Low Stock").length,
        [dateFilteredItems, receivedQtyMap]
    );

    const outOfStockCount = useMemo(
        () => dateFilteredItems.filter((i) => getStockStatus(i, receivedQtyMap) === "Out of Stock").length,
        [dateFilteredItems, receivedQtyMap]
    );
    // ---- Stock overview donut ----
    const stockOverviewData = useMemo(() => {
        const counts = { "In Stock": 0, "Low Stock": 0, "Out of Stock": 0, "Inactive": 0 };
        dateFilteredItems.forEach((i) => { counts[getStockStatus(i, receivedQtyMap)]++; });
        return Object.entries(counts)
            .filter(([, v]) => v > 0)
            .map(([name, value]) => ({ name, value, color: STOCK_STATUS_COLORS[name] }));
    }, [dateFilteredItems, receivedQtyMap]);
    const stockOverviewTotal = stockOverviewData.reduce((a, b) => a + b.value, 0);

    // ---- Top categories ----
    const topCategoriesData = useMemo(() => {
        const map = {};
        dateFilteredItems.forEach((i) => {
            const key = i.category || "Uncategorized";
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, value], i) => ({ name, value, color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [dateFilteredItems]);
    const topCategoriesTotal = topCategoriesData.reduce((a, b) => a + b.value, 0);


    const categoryOptions = [...new Set(visibleItems.map((i) => i.category).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const supplierOptions = [...new Set(visibleItems.map((i) => i.supplier).filter(Boolean))].map((s) => ({ label: s, value: s }));


    const statusOptions = ["In Stock", "Low Stock", "Out of Stock", "Inactive"].map((s) => ({ label: s, value: s }));

    const REPORT_COLUMNS = [
        { label: "Item Name" },
        { label: "SKU / Barcode" },
        { label: "Category" },
        { label: "Unit" },
        { label: "Current Stock" },
        { label: "Cost Price (Rs.)" },
        { label: "Stock Status" },
        { label: "Last Updated" },
    ];

    const buildReportRows = () => filteredItems.map((r) => {
        const stock = Number(receivedQtyMap[String(r.id)] ?? r.current_stock ?? 0);
        return {
            "Item Name": r.item_name || "-",
            "SKU / Barcode": r.sku_barcode || "-",
            "Category": r.category || "-",
            "Unit": r.unit || "-",
            "Current Stock": stock,
            "Cost Price (Rs.)": Number(r.cost_price || 0),
            "Stock Status": getStockStatus(r, receivedQtyMap),
            "Last Updated": r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB') : '-',
        };
    });

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory Report");
        XLSX.writeFile(wb, `Inventory-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Inventory Dashboard Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Inventory-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: 'SI No', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            render: (v, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{record.sku_barcode}</div>
                </div>
            ),
        },
        { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => v || '-' },
        { title: 'Unit', dataIndex: 'unit', key: 'unit', render: (v) => v || '-' },
        {
            title: 'Current Stock',
            dataIndex: 'current_stock',
            key: 'current_stock',
            align: 'right',
            render: (v, record) => {
                const stock = Number(receivedQtyMap[String(record.id)] ?? v ?? 0);
                return <span style={{ color: stock < 0 ? '#f5222d' : 'inherit', fontWeight: stock < 0 ? 600 : 400 }}>{stock.toLocaleString('en-IN')}</span>;
            },
        },
        {
            title: 'Available Stock',
            dataIndex: 'available_stock',                                         
            key: 'available_stock',
            align: 'right',
            render: (v, record) => {
                const stock = Number(receivedQtyMap[String(record.id)] ?? v ?? record.current_stock ?? 0);
                return <span style={{ color: stock < 0 ? '#f5222d' : 'inherit', fontWeight: stock < 0 ? 600 : 400 }}>{stock.toLocaleString('en-IN')}</span>;
            },
        },
        // {
        //     title: 'Cost / Selling Price',
        //     key: 'pricing',
        //     render: (_, record) => (
        //         <div style={{ fontSize: 12 }}>
        //             <div>Cost: ₹{Number(record.cost_price || 0).toLocaleString('en-IN')}</div>
        //             <div style={{ color: '#888' }}>Sell: ₹{Number(record.selling_price || 0).toLocaleString('en-IN')}</div>
        //         </div>
        //     ),
        // },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = getStockStatus(record, receivedQtyMap);
                const color = STOCK_STATUS_COLORS[status];
                return (
                    <span style={{
                        background: `${color}1a`, color, padding: '3px 10px',
                        borderRadius: 6, fontWeight: 600, fontSize: 12,
                    }}>
                        {status}
                    </span>
                );
            },
        },
        {
            title: 'Last Updated',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-',
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/view-item', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/edit-item', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Header — mirrors ProjectsPage's header pattern */}
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Inventory Dashboard</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Inventory Dashboard</Typography.Text>
                </div>
         <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu
                                onClick={({ key }) => {
                                    let range = null;
                                    let label = 'All Time';
                                    if (key === 'today') {
                                        range = [dayjs().startOf('day'), dayjs().endOf('day')];
                                        label = 'Today';
                                    } else if (key === 'last7') {
                                        range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                                        label = 'Last 7 Days';
                                    } else if (key === 'last30') {
                                        range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                                        label = 'Last 30 Days';
                                    } else if (key === 'lastMonth') {
                                        range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                                        label = 'Last Month';
                                    } else if (key === 'thisYear') {
                                        range = [dayjs().startOf('year'), dayjs()];
                                        label = 'This Year';
                                    } else if (key === 'all') {
                                        range = null;
                                        label = 'All Time';
                                    }
                                    setDateFilterRange(range);
                                    setDateFilterLabel(label);
                                }}
                                items={[
                                    { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                                    { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                                    { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                                    { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                                    { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                                    { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                                ]}
                            />
                        }
                    >
                        <Button size="middle" icon={<DownOutlined />}>
                            More Actions ({dateFilterLabel})
                        </Button>
                    </Dropdown>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/add-item', { state: { action: 'Add', enableEdit: true, from: '/inventory' } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            Add Item
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            {/* Stat cards */}
            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Items" value={totalItems} sub="View all items" color="#1677ff" bg="#f0f7ff" icon={<AppstoreOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Stock Value" value={`₹ ${totalStockValue.toLocaleString('en-IN')}`} sub="View stock valuation" color="#52c41a" bg="#f6ffed" icon={<DollarOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Low Stock Items" value={lowStockCount} sub="View low stock" color="#fa8c16" bg="#fff7e6" icon={<WarningOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Out of Stock Items" value={outOfStockCount} sub="View out of stock" color="#f5222d" bg="#fff1f0" icon={<StopOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Pending POs" value={pendingPOsCount} sub="View purchase orders" color="#1658b4" bg="#eef4ff" icon={<FileTextOutlined />} />
                    </Col>
                </Row>
            </div>

            {/* Main content: left = filters + table, right = Stock Overview / Top Categories sidebar */}
            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        {/* Filters */}
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={7}>
                                    <Input
                                        allowClear
                                        placeholder="Search item name / SKU / barcode..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Categories" options={categoryOptions}
                                        value={selectedCategory} onChange={setSelectedCategory} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Suppliers" options={supplierOptions}
                                        value={selectedSupplier} onChange={setSelectedSupplier} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredItems.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                            columns={columns}
                        />
                    </Col>

                    {/* Right sidebar — Stock Overview & Top Categories */}
                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Stock Overview</Typography.Text>
                            {stockOverviewTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📦</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${stockOverviewTotal ? Math.round((v / stockOverviewTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={stockOverviewData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {stockOverviewData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{
                                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                            textAlign: 'center', pointerEvents: 'none',
                                        }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{stockOverviewTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total Items</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {stockOverviewData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({stockOverviewTotal ? Math.round((e.value / stockOverviewTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Top Categories</Typography.Text>
                            {topCategoriesTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🗂️</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ marginTop: 12 }}>
                                    {topCategoriesData.map((c, i) => {
                                        const pct = topCategoriesTotal ? Math.round((c.value / topCategoriesTotal) * 100) : 0;
                                        return (
                                            <div key={i} style={{ marginBottom: 10 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                                    <span style={{ color: '#333' }}>{c.name}</span>
                                                    <span style={{ fontWeight: 700, color: '#333' }}>{c.value} ({pct}%)</span>
                                                </div>
                                                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                                                    <div style={{ width: `${pct}%`, height: '100%', background: c.color, borderRadius: 3 }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default InventoryDashboard;